import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import CheckoutButton from "@/components/CheckoutButton";
import PromoCodeInput from "@/components/PromoCodeInput";
import { useAuth } from "@/lib/AuthContext";
import { Check } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

export default function Subscriptions() {
    const [data, setData] = useState(null);
    const [yearly, setYearly] = useState(false);
    const { user } = useAuth();
    const [promo, setPromo] = useState(() => user?.signup_promo_code || "");
    const [cancelOpen, setCancelOpen] = useState(false);
    const [portalBusy, setPortalBusy] = useState(false);

    useEffect(() => {
        api.get("/member/subscriptions").then((r) => setData(r.data));
    }, []);

    // Cancellation itself happens on Stripe's hosted Billing Portal, same
    // as the "Manage payment method" flow — Stripe's portal already has a
    // proper cancel-subscription UI (and its cancel-timing behaviour is
    // controlled by the portal's configuration in the Stripe Dashboard).
    // Note: membershipInForce won't flip off until the subscription-
    // lifecycle webhook exists (tracked separately) — clicking through
    // here doesn't update this app's own "active" status by itself yet.
    const goToBillingPortal = async () => {
        setPortalBusy(true);
        try {
            const { data: portal } = await api.post("/member/billing-portal", {
                return_url: window.location.href,
            });
            window.location.href = portal.url;
        } catch (err) {
            toast.error(formatApiError(err));
            setPortalBusy(false);
        }
    };

    if (!data) return <div className="p-10 text-navy-500">Loading…</div>;
    const plan = data.current_plan;
    const active = data.membership_active;

    return (
        <>
            <PageHeader
                eyebrow="Membership"
                title="Your Perx subscription"
                description={
                    active
                        ? `Your membership is active. Renews on ${data.renews_on}.`
                        : "You don't have an active membership yet."
                }
                testid="subscriptions-header"
            />

            <div className="px-6 lg:px-12 py-8">
                <div
                    data-testid={`sub-plan-${plan.id}`}
                    className="rounded-3xl bg-navy-700 text-white shadow-lift p-10 lg:p-14 grid md:grid-cols-[1fr_1px_1fr] gap-10 items-center"
                >
                    <div>
                        <div
                            className={`inline-flex items-center rounded-full text-[10px] uppercase tracking-widest font-semibold px-3 py-1 mb-4 ${
                                active
                                    ? "bg-gold-500 text-navy-900"
                                    : "bg-white/10 text-navy-50/70"
                            }`}
                        >
                            {active ? "Active subscription" : "No active subscription"}
                        </div>
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-500 mb-3">
                            The Perx Membership
                        </div>
                        {/* Billing Cycle Toggle */}
                        {!active && (
                            <div className="inline-flex items-center rounded-full bg-white/10 p-1 mb-4">
                                <button
                                    type="button"
                                    data-testid="sub-billing-monthly"
                                    onClick={() => setYearly(false)}
                                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                        !yearly
                                            ? "bg-gold-500 text-navy-900"
                                            : "text-navy-50/70 hover:text-white"
                                    }`}
                                >
                                    Monthly
                                </button>
                                <button
                                    type="button"
                                    data-testid="sub-billing-yearly"
                                    onClick={() => setYearly(true)}
                                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                        yearly
                                            ? "bg-gold-500 text-navy-900"
                                            : "text-navy-50/70 hover:text-white"
                                    }`}
                                >
                                    Yearly <span className="text-gold-400 ml-1">save more</span>
                                </button>
                            </div>
                        )}
                        <div className="font-display text-6xl mb-2">
                            ${!active && yearly
                                ? Math.round(plan.price_yearly / 12)
                                : plan.price_monthly}
                            <span className="text-base font-sans text-navy-50/60">
                                {" "}/ mo
                            </span>
                        </div>
                        <div className="text-navy-50/70 text-lg mb-6">
                            {plan.tagline}
                        </div>
                        {active ? (
                            <div className="text-sm text-navy-50/70 mb-5">
                                Renews on <strong className="text-white">{data.renews_on}</strong>. Cancel any time.
                            </div>
                        ) : (
                            <div className="text-sm text-navy-50/70 mb-5">
                                Subscribe to unlock member pricing on every deal.
                            </div>
                        )}
                        {active ? (
                            <Button
                                type="button"
                                variant="outline"
                                data-testid="sub-cancel"
                                onClick={() => setCancelOpen(true)}
                                className="rounded-full border-white/30 text-white hover:bg-white/10 h-11 px-6 font-semibold bg-transparent"
                            >
                                Cancel subscription
                            </Button>
                        ) : (
                            <div className="space-y-3">
                                <PromoCodeInput
                                    context="signup"
                                    value={promo}
                                    onApplied={setPromo}
                                    testidPrefix="sub-promo"
                                    className="max-w-xs text-red-600"
                                />
                                <CheckoutButton
                                    kind="membership"
                                    interval={yearly ? "yearly" : "monthly"}
                                    customerEmail={user?.email}
                                    userId={user?.id}
                                    promoCode={promo || undefined}
                                    testid="sub-manage"
                                    className="rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 h-11 px-6 font-semibold"
                                >
                                    Subscribe now
                                </CheckoutButton>
                            </div>
                        )}
                    </div>
                    <div className="hidden md:block h-full w-px bg-white/10" />
                    <ul className="space-y-3 text-sm text-navy-50/90">
                        {plan.features.map((f) => (
                            <li key={f} className="flex gap-3">
                                <Check size={18} className="text-gold-500 mt-0.5" />
                                {f}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
{/* Add Cancel Subscription Dialog */}
            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
                <DialogContent
                    data-testid="sub-cancel-dialog"
                    className="max-w-md bg-white"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl text-navy-700">
                            Cancel your membership?
                        </DialogTitle>
                        <DialogDescription>
                            You'll keep member pricing and access through the end of
                            your current billing period — it just won't renew after
                            that. You'll finish cancelling on Stripe's secure billing
                            page.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        {/* Keep Membership Button */}
                        <Button
                            variant="outline"
                            onClick={() => setCancelOpen(false)}
                            className="rounded-full"
                        >
                            Keep my membership
                        </Button>
                        {/* Cancel Subscription Button - On Stripe */}
                        <Button
                            onClick={goToBillingPortal}
                            disabled={portalBusy}
                            data-testid="sub-cancel-confirm"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white"
                        >
                            {portalBusy ? "Opening…" : "Continue to cancel"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
